import React, { useState, useEffect } from 'react'
import axios from 'axios';
import { toast } from 'sonner'

import { Progress, Tooltip } from "@material-tailwind/react";
import { HiTrash } from "react-icons/hi2"
import { BiLoaderCircle } from "react-icons/bi";
import { ArrowDownTrayIcon, ArrowPathIcon, ArrowPathRoundedSquareIcon, CheckCircleIcon,
    ExclamationTriangleIcon, InformationCircleIcon, PlayIcon } from "@heroicons/react/24/solid"
import exportResults from '../../../../middlewares/Listings/exportResults';
import DataTableShell from '../../../../Components/table/DataTableShell';
import TableToolbar from '../../../../Components/table/TableToolbar';
import TableSearch from '../../../../Components/table/TableSearch';
import {
    TableActionGroup,
    TableEmptyRow,
    TableIdCell,
    TableSkeletonRows,
    TableStatusChip,
} from '../../../../Components/table/TableCells';
import useClientTable from '../../../../Components/table/useClientTable';

const TABLE_HEAD = ["Request Id", "Uploaded by", "Site Category", "Brand", "Status", "Actions"];
const filterRequest = (item, query) =>
    !query ||
    item.batchId?.toString().toLowerCase().includes(query) ||
    item.addedBy?.toString().toLowerCase().includes(query) ||
    item.brandName?.toLowerCase().includes(query);
const sortRequests = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

export function RequestsTable() {

    const [requestData, setRequestData] = useState([])
    const [isLoad, setIsLoad] = useState(true)
    const [isAltered, setIsAltered] = useState(false)
    const [isAltereds, setIsAltereds] = useState()
    const [downloadingBatch, setDownloadingBatch] = useState({});

    const requestsPerPage = 10
    const {
        searchTerm,
        handleSearch,
        currentPage,
        setCurrentPage,
        pageItems: requestDataArr,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: requestData,
        pageSize: requestsPerPage,
        filterFn: filterRequest,
        sortFn: sortRequests,
    });

    useEffect(() => {
        axios.get(`/api/scraper-requests/getRequests`)
        .then(response => {
            if (response.data.message) {
                console.log("Cannot get batches")
            } else {
                setRequestData(response.data.requests)
            }
            setIsLoad(false)
        })
        .catch(error => {
            console.error('Error:', error)
            setIsLoad(false)
        }) 

    }, [isAltered, isAltereds])

    useEffect(() => {
        const toggleInterval = setInterval(() => {
            setIsAltereds(prevState => !prevState)
        }, 5000) 
        return () => {
            clearInterval(toggleInterval)
        }
    }, [])

    const handleDeleteRequest = (batchId) => {
        const isConfirmed = window.confirm(`Are you sure you want to this batch: ${batchId}?`)

        if (isConfirmed) {
            axios.delete(`/api/scraper-requests/delete/${batchId}`)
                .then(response => {
                    if (response.data && response.data.message) {
                        toast.success(response.data.message)
                        axios.delete(`/api/scraper-results/delete/${batchId}`)
                        .then(response => {
                            console.log(response.data)
                        })
                        .catch(error => {
                            console.error('Error:', error);
                        })
                    } else {
                        toast.error('Deletion was unsuccessful. Please try again.')
                    }
                    setIsAltered(prevState => !prevState)
                })
                .catch(error => {
                    toast.error('An error occurred while deleting the scraper.')
                    console.error('Error:', error)
                })
        }
    }

    const resetProgress = (batchId) => {
        axios.put(`/api/scraper-requests/update/progress/${batchId}`, { progress: 0 })
            .then(() => {
                //toast.success(message);
                console.log(`reset progress: ${batchId}`);
            })
            .catch(error => {
                toast.error("Failed to reset progress");
                console.error('Error:', error);
            });
    };

    const handleReopenRequest = (batchId) => {

        const updatedStatus = {
            status: {
                name: 'Open',
                message: 'This batch is reopened'
            }
        }
        axios.put(`/api/scraper-requests/update/status/${batchId}`, updatedStatus)
        .then( response => {
            console.log(response)
            if (response.data.message){
                toast.success('Batch reopened')
                resetProgress(batchId)
                axios.delete(`/api/scraper-results/delete/${batchId}`)
                .then(response => {
                    console.log(response.data)
                })
                .catch(error => {
                    console.error('Error:', error);
                })
                axios.post(`/api/scraper-watcher/start-watching`)
                .then(watcherResponse => {
                    if (watcherResponse.data.message) {
                        //toast.info('Watcher started successfully.')
                        console.log('Watcher started successfully')
                    }
                })
                .catch(watcherError => {
                    console.error('Error starting watcher:', watcherError);
                    const watcherErrorMessage = watcherError.response?.data?.message || 'Failed to start watcher.';
                    console.log(watcherErrorMessage)
                    //toast.error(watcherErrorMessage);
                });
            } else {
                toast.error(response.data.message)
            }
        })
        .catch(error => {
            toast.error(error)
            console.error('Error:', error)
        })
    }

    const handleStartWatcher = () => {
        axios.post('/api/scraper-watcher/start-watching')
            .then((response) => {
                toast.success(response.data.message || 'Watcher started.')
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || 'Failed to start watcher.')
                console.error('Error starting watcher:', error)
            })
    }

    const handleDownloadRequest = async (batchId, brand, siteCategory) => {
        setDownloadingBatch(prevState => ({ ...prevState, [batchId]: true }));
        await exportResults({ batchId, brand, siteCategory })
        setDownloadingBatch(prevState => ({ ...prevState, [batchId]: false }));
    }

    return (
        <DataTableShell
            minWidth="900px"
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            toolbar={
                <TableToolbar
                    kicker="Webscrapers"
                    title="Request history"
                    subtitle={`${totalFiltered} matching requests · ${requestData.length} total · refreshes every 5 seconds`}
                >
                    <TableSearch
                        id="scraper-requests-search"
                        value={searchTerm}
                        onChange={handleSearch}
                        placeholder="Search requests"
                    />
                    <button
                        type="button"
                        className="nx-btn-secondary"
                        onClick={handleStartWatcher}
                        title="Process all Open batches"
                    >
                        <PlayIcon className="h-4 w-4" />
                        Process open batches
                    </button>
                </TableToolbar>
            }
        >
            <thead>
                <tr>
                    {TABLE_HEAD.map((head) => <th key={head}>{head}</th>)}
                </tr>
            </thead>
            <tbody>
                {isLoad ? (
                    <TableSkeletonRows columns={TABLE_HEAD.length} rows={requestsPerPage} />
                ) : requestDataArr.length === 0 ? (
                    <TableEmptyRow
                        columns={TABLE_HEAD.length}
                        title="No requests found"
                        description="Try a different batch ID, uploader, or brand."
                    />
                ) : requestDataArr.map((item) => {
                    const date = new Date(item.createdAt)
                    const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                    const options = { month: 'long', day: 'numeric', year: 'numeric' }
                    const formattedDate = date.toLocaleDateString('en-US', options)
                    const formattedDateTime = `${formattedDate} ${formattedTime}`
                    const statusName = item.status?.name || 'Unknown'

                    return (
                        <tr key={item.batchId}>
                            <td><TableIdCell>{item.batchId}</TableIdCell></td>
                            <td>
                                <div className="flex items-center gap-1 text-muted">
                                    {item.addedBy}
                                    <Tooltip content={formattedDateTime}>
                                        <InformationCircleIcon className="h-4 w-4" />
                                    </Tooltip>
                                </div>
                            </td>
                            <td><span className="nx-chip-idle">{item.siteCategory}</span></td>
                            <td className="font-medium text-paper">{item.brandName}</td>
                            <td>
                                <div className="flex min-w-[12rem] flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <TableStatusChip status={statusName} />
                                        <span className="font-mono text-xs text-muted">{item.progress}%</span>
                                        <Tooltip content={item.status?.message}>
                                            {statusName === 'Processing' ? <ArrowPathIcon className="h-4 w-4 animate-spin text-warning" /> :
                                                statusName === 'Complete' ? <CheckCircleIcon className="h-4 w-4 text-success" /> :
                                                statusName === 'Error' ? <ExclamationTriangleIcon className="h-4 w-4 text-danger" /> :
                                                <InformationCircleIcon className="h-4 w-4 text-signal" />}
                                        </Tooltip>
                                    </div>
                                    <Progress
                                        value={item.progress}
                                        variant="gradient"
                                        size="sm"
                                        color={item.progress !== 100 ? 'orange' : 'green'}
                                    />
                                </div>
                            </td>
                            <td>
                                <TableActionGroup>
                                    {statusName !== 'Open' && (
                                        <>
                                            {statusName !== 'Processing' && (
                                                <button
                                                    type="button"
                                                    className="nx-btn-ghost p-2 text-signal"
                                                    onClick={() => handleDownloadRequest(item.batchId, item.brandName, item.siteCategory)}
                                                    title="Download results"
                                                >
                                                    {downloadingBatch[item.batchId] ? <BiLoaderCircle className="h-5 w-5 animate-spin" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
                                                    <span className="sr-only">Download results</span>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="nx-btn-ghost p-2 text-warning"
                                                onClick={() => handleReopenRequest(item.batchId)}
                                                title="Reopen batch"
                                            >
                                                <ArrowPathRoundedSquareIcon className="h-5 w-5" />
                                                <span className="sr-only">Reopen batch</span>
                                            </button>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className="nx-btn-ghost p-2 text-danger"
                                        onClick={() => handleDeleteRequest(item.batchId)}
                                        title="Delete batch"
                                    >
                                        <HiTrash className="h-5 w-5" />
                                        <span className="sr-only">Delete batch</span>
                                    </button>
                                </TableActionGroup>
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </DataTableShell>
    );
}
